import {
  Button,
  Input,
  InputNumber,
  Select,
  Space,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  DeleteOutlined,
  DownOutlined,
  PlusOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProcessChoice, ProcessRole } from "../../api";
import ConditionBuilder from "./ConditionBuilder";
import { describeCondition, isConditional } from "./conditionText";
import { ASSIGNMENT_LABEL, labelFor } from "./terms";
import type { FieldRef } from "./types";

const { Text } = Typography;

type Props = {
  roles: ProcessRole[];
  onChange: (roles: ProcessRole[]) => void;
  assignments: ProcessChoice[];
  groups: Array<{ id: number; name: string }>;
  /** For the per-slot rule builder. Absent = no rules offered, which is what
   *  the template setup screen wants: it has no field list to pick from. */
  fields?: FieldRef[];
  ops?: string[];
  orderOps?: string[];
  /** The org chart, for the "head of a unit" mode. Absent = the derived modes
   *  render their settings without a unit list, which is what the template
   *  setup screen gets (it has no org payload to hand). */
  orgUnits?: Array<{ id: number; code: string; name: string; has_head: boolean }>;
  /** Which modes read the org chart. From the server so the two cannot drift. */
  derivedAssignments?: string[];
  /** Position of the step this sign-off sits in, so a rule reading an answer
   *  given later can be called out where it is written. */
  stageIndex?: number;
  disabled?: boolean;
};

/** Assignment modes that resolve their approvers from a group. */
const NEEDS_POOL = ["ManualFromPool", "AutoQuorum", "AutoAll"];

/** Fallback for the derived modes if the server did not say which they are.
 *  Only reached by a client talking to an older backend; the real list is
 *  `choices.derived_assignments`. */
const DERIVED_FALLBACK = ["AutoManager", "AutoUnitHead"];

/** One line saying what a slot will actually do, given how it is set up.
 *
 *  The derived modes get their sentence from here too rather than from the
 *  server's `resolver_text`: that arrives interpolated and untranslated, and
 *  is only correct until the next keystroke. The server's copy
 *  (`main/assignment.describe`) is what goes into the history diff and into a
 *  refusal message; this one is what the editor shows while you type. */
const explain = (role: ProcessRole, groupName: string | undefined) => {
  const resolver = role.resolver || {};
  switch (role.assignment) {
    case "ManualFromPool":
      return groupName
        ? "The requester picks one person from {{group}}, and that person signs."
        : "Pick the group they are chosen from.";
    case "AutoQuorum":
      return groupName
        ? "Everyone in {{group}} is invited; any {{count}} of them signing satisfies this slot."
        : "Pick the group to invite.";
    case "AutoAll":
      return groupName
        ? "Everyone in {{group}} is invited, and all of them must sign."
        : "Pick the group to invite.";
    case "AutoManager": {
      const levels = Number(resolver.levels) || 1;
      if (levels === 1)
        return "Whoever the requester reports to signs — worked out when the case reaches this step.";
      if (levels === 2)
        return "The requester's manager's manager signs.";
      return "The person {{levels}} levels above the requester signs.";
    }
    case "AutoUnitHead":
      if (resolver.unit_source === "field")
        return "Whoever heads the unit named in the answer to '{{field}}' signs.";
      if (resolver.unit_source === "fixed")
        return "Whoever heads {{unit}} signs, whoever raised the case.";
      return "Whoever heads the requester's own unit signs.";
    default:
      return "The requester picks anyone at all, and that person signs.";
  }
};

/**
 * The named signature slots on one sign-off — "Permit Issuer", "Safety Watch".
 *
 * This was a five-column table, which is fine on a wide page and unreadable in
 * an inspector panel: the group picker got about 90px and the explanation of
 * what each mode *means* had nowhere to go at all. The modes are the hard part
 * -- "quorum" invites everybody and takes any N, which is not guessable from
 * the word -- so each slot is now a small card that says, in a sentence, what
 * it will do once a case reaches it.
 *
 * A sign-off with no slots keeps the older behaviour: pick any N approvers with
 * no record of which duty each discharged, which is why the empty state says so
 * rather than looking like a mistake.
 */
const RoleSlots = ({
  roles,
  onChange,
  assignments,
  groups,
  fields,
  ops,
  orderOps,
  orgUnits,
  derivedAssignments,
  stageIndex = -1,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  // Which slots have their rule builder open. Collapsed by default and keyed by
  // position: an authority matrix is five slots of which one is conditional, so
  // five open builders would bury the thing they qualify.
  const [openRules, setOpenRules] = useState<Record<number, boolean>>({});
  const canCondition = Boolean(fields && ops && orderOps);
  const derived = derivedAssignments?.length ? derivedAssignments : DERIVED_FALLBACK;

  const update = (index: number, patch: Partial<ProcessRole>) =>
    onChange(roles.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const remove = (index: number) => onChange(roles.filter((_, i) => i !== index));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= roles.length) return;
    const next = [...roles];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const add = () =>
    onChange([
      ...roles,
      {
        id: null,
        title: "",
        assignment: "ManualAny",
        pool: null,
        quorum: 1,
        condition: {},
        resolver: {},
      },
    ]);

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("Signature slots")}
      </Text>

      {roles.length === 0 && (
        <div
          style={{
            padding: 10,
            borderRadius: token.borderRadius,
            border: `1px dashed ${token.colorBorder}`,
            color: token.colorTextTertiary,
            fontSize: 12,
          }}
        >
          {t(
            "No named slots. The requester picks any approvers and the sign-off finishes on a count — nothing records which duty each person discharged."
          )}
        </div>
      )}

      {roles.map((role, index) => {
        const isDerived = derived.includes(role.assignment);
        const needsPool = !isDerived && NEEDS_POOL.includes(role.assignment);
        const groupName = groups.find((g) => g.id === role.pool)?.name;
        const resolver = role.resolver || {};
        const unitSource = resolver.unit_source || "requester";
        const pickedUnit = orgUnits?.find((u) => u.code === resolver.unit);
        return (
          <div
            key={index}
            style={{
              padding: 10,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorderSecondary}`,
              background: token.colorFillQuaternary,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 18,
                  height: 18,
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  background: token.colorBgContainer,
                  border: `1px solid ${token.colorBorder}`,
                  fontSize: 10,
                }}
              >
                {index + 1}
              </span>
              <Input
                size="small"
                value={role.title}
                disabled={disabled}
                status={role.title.trim() ? undefined : "error"}
                placeholder={t("e.g. Permit Issuer")}
                onChange={(e) => update(index, { title: e.target.value })}
              />
              <Space size={0}>
                <Button
                  size="small"
                  type="text"
                  icon={<UpOutlined />}
                  disabled={disabled || index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={t("Move earlier")}
                />
                <Button
                  size="small"
                  type="text"
                  icon={<DownOutlined />}
                  disabled={disabled || index === roles.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={t("Move later")}
                />
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={disabled}
                  onClick={() => remove(index)}
                  aria-label={t("Remove this slot")}
                />
              </Space>
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <Select
                size="small"
                style={{ flex: 1, minWidth: 0 }}
                value={role.assignment}
                disabled={disabled}
                onChange={(v) =>
                  update(index, {
                    assignment: v,
                    pool:
                      !derived.includes(v) && NEEDS_POOL.includes(v)
                        ? role.pool
                        : null,
                    // Cleared when leaving a derived mode, so switching away
                    // and back cannot resurrect a stale {"levels": 3} that
                    // nobody can see and that would quietly take effect.
                    resolver: derived.includes(v) ? role.resolver || {} : {},
                  })
                }
                options={assignments.map((a) => ({
                  value: a.value,
                  label: t(labelFor(ASSIGNMENT_LABEL, a.value)),
                }))}
              />
              {role.assignment === "AutoQuorum" && (
                <Tooltip title={t("How many of them have to sign")}>
                  <InputNumber
                    size="small"
                    min={1}
                    style={{ width: 68 }}
                    value={role.quorum}
                    disabled={disabled}
                    onChange={(v) => update(index, { quorum: Number(v) || 1 })}
                  />
                </Tooltip>
              )}
            </div>

            {needsPool && (
              <Select
                size="small"
                showSearch
                optionFilterProp="label"
                style={{ width: "100%", marginTop: 6 }}
                placeholder={t("Pick a group")}
                value={role.pool ?? undefined}
                disabled={disabled}
                status={role.pool ? undefined : "error"}
                onChange={(v) => update(index, { pool: v })}
                options={groups.map((g) => ({ value: g.id, label: g.name }))}
              />
            )}

            {/* A derived slot has no group picker: the org chart is the pool.
                What it needs instead is how far up, or which unit. */}
            {role.assignment === "AutoManager" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 6,
                }}
              >
                <span style={{ fontSize: 11, color: token.colorTextTertiary }}>
                  {t("How far up")}
                </span>
                <Select
                  size="small"
                  style={{ flex: 1, minWidth: 0 }}
                  value={Number(resolver.levels) || 1}
                  disabled={disabled}
                  onChange={(v) =>
                    update(index, { resolver: { ...resolver, levels: v } })
                  }
                  options={[
                    { value: 1, label: t("Their manager") },
                    { value: 2, label: t("Their manager's manager") },
                    { value: 3, label: t("Three levels up") },
                    { value: 4, label: t("Four levels up") },
                  ]}
                />
              </div>
            )}

            {role.assignment === "AutoUnitHead" && (
              <div style={{ marginTop: 6 }}>
                <Select
                  size="small"
                  style={{ width: "100%" }}
                  value={unitSource}
                  disabled={disabled}
                  onChange={(v) =>
                    update(index, {
                      resolver: { ...resolver, unit_source: v },
                    })
                  }
                  options={[
                    {
                      value: "requester",
                      label: t("The unit the requester belongs to"),
                    },
                    {
                      value: "field",
                      label: t("The unit named in an answer"),
                    },
                    { value: "fixed", label: t("Always the same unit") },
                  ]}
                />
                {unitSource === "field" && (
                  <Select
                    size="small"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%", marginTop: 6 }}
                    placeholder={t("Which question names the unit?")}
                    value={resolver.field || undefined}
                    disabled={disabled}
                    status={resolver.field ? undefined : "error"}
                    onChange={(v) =>
                      update(index, { resolver: { ...resolver, field: v } })
                    }
                    options={(fields ?? []).map((f) => ({
                      value: f.name,
                      label: `${f.label || f.name} — ${f.sectionTitle}`,
                    }))}
                  />
                )}
                {unitSource === "fixed" && (
                  <Select
                    size="small"
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%", marginTop: 6 }}
                    placeholder={t("Pick a unit")}
                    value={resolver.unit || undefined}
                    disabled={disabled}
                    status={resolver.unit ? undefined : "error"}
                    onChange={(v) =>
                      update(index, { resolver: { ...resolver, unit: v } })
                    }
                    options={(orgUnits ?? []).map((u) => ({
                      value: u.code,
                      label: u.has_head ? u.name : `${u.name} — ${t("no head")}`,
                    }))}
                  />
                )}
                {/* The choice is legal and the process would publish; it is
                    the *case* that would stop, months later, on a step with no
                    button. Said here, at the moment of the choice. */}
                {unitSource === "fixed" && pickedUnit && !pickedUnit.has_head && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      color: token.colorWarningText,
                    }}
                  >
                    {t(
                      "Nobody heads {{unit}} yet, so no case will get past this slot until somebody does.",
                      { unit: pickedUnit.name },
                    )}
                  </div>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 5,
                marginTop: 8,
                color: token.colorTextTertiary,
                fontSize: 11,
              }}
            >
              <UserOutlined style={{ marginTop: 2 }} />
              <span>
                {t(explain(role, groupName), {
                  group: groupName ?? "",
                  count: role.quorum,
                  levels: Number(resolver.levels) || 1,
                  field:
                    fields?.find((f) => f.name === resolver.field)?.label ??
                    resolver.field ??
                    "",
                  unit: pickedUnit?.name ?? resolver.unit ?? "",
                })}
              </span>
            </div>

            {canCondition && (
              <div style={{ marginTop: 8 }}>
                {/* "The CFO signs only over 50 000" is a property of one
                    signature line, not of a step. Modelling it as a
                    conditional STEP forces one approver per step and turns an
                    authority matrix into a row of near-empty boxes. */}
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: "auto", fontSize: 11 }}
                  disabled={disabled}
                  onClick={() =>
                    setOpenRules((o) => ({ ...o, [index]: !o[index] }))
                  }
                >
                  {isConditional(role.condition)
                    ? t("Only when: {{rule}}", {
                        rule: describeCondition(role.condition, fields!, t),
                      })
                    : t("Always asked for — add a rule")}
                </Button>
                {(openRules[index] || isConditional(role.condition)) && (
                  <div style={{ marginTop: 6 }}>
                    <ConditionBuilder
                      value={role.condition ?? {}}
                      onChange={(condition) => update(index, { condition })}
                      fields={fields!}
                      stageIndex={stageIndex}
                      ops={ops!}
                      orderOps={orderOps!}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <Button
        size="small"
        icon={<PlusOutlined />}
        disabled={disabled}
        onClick={add}
        block
      >
        {t("Add a signature slot")}
      </Button>
    </Space>
  );
};

export default RoleSlots;
